import {
  ActionConfirmationModal,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardLineItem,
  CardTitle,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useDeleteFirmOffice } from "@/lib/data/react-query";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type FirmOffice } from "@/lib/types";
import { formatLocalOrIntlPhoneNumber } from "@/lib/utils/format";
import { Edit, Trash } from "lucide-react";
import { useState } from "react";

interface OfficeDetailsProps {
  isLoading: boolean;
  office: FirmOffice;
  onEdit: () => void;
  onDelete: () => void;
}

export function OfficeDetails({
  isLoading,
  office,
  onEdit,
  onDelete,
}: OfficeDetailsProps) {
  const { hasActionPermission } = useUser();

  const [isDeleting, setIsDeleting] = useState(false);
  const deleteOffice = useDeleteFirmOffice();
  const handleDelete = () => {
    onDelete();
    deleteOffice.mutate({
      id: office.id,
    });
  };
  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>{office.label}</CardTitle>
          <div className="space-x-2">
            {hasActionPermission(
              ACTION_PERMISSION_KEYS.firmOffice,
              ACTION_PERMISSION_VALUES.update,
            ) && (
              <Button
                data-testid="firm-office-edit-button"
                onClick={onEdit}
                variant="outline"
                size="sm"
                disabled={!office.id || office.id === "temp"}
              >
                <Edit className="h-4 w-4" />
                <Text as="span" variant="body" className="hidden md:block">
                  Edit
                </Text>
              </Button>
            )}
            {hasActionPermission(
              ACTION_PERMISSION_KEYS.firmOffice,
              ACTION_PERMISSION_VALUES.delete,
            ) && (
              <Button
                onClick={() => {
                  setIsDeleting(true);
                }}
                variant="destructive"
                size="sm"
                disabled={!office.id || office.id === "temp"}
              >
                <Trash className="h-4 w-4" />
                <Text
                  as="span"
                  variant="body"
                  color="white"
                  className="hidden md:block"
                >
                  Delete
                </Text>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <dl className="divide-primary-foreground divide-y">
            <CardLineItem
              isLoading={isLoading}
              label="Phone number"
              value={formatLocalOrIntlPhoneNumber({
                number: office.phoneNumber,
              })}
            />
            <CardLineItem
              isLoading={isLoading}
              label="Address 1"
              value={office.street1}
            />
            <CardLineItem
              isLoading={isLoading}
              label="Address 2"
              value={office.street2 ?? undefined}
            />
            <CardLineItem
              isLoading={isLoading}
              label="City"
              value={office.city}
            />
            <CardLineItem
              isLoading={isLoading}
              label="State"
              value={office.state}
            />
            <CardLineItem
              isLoading={isLoading}
              label="Zip code"
              value={office.zipCode}
            />
            <CardLineItem
              isLoading={isLoading}
              label="Country"
              value={office.country}
            />
          </dl>
        </CardContent>
        <CardFooter className="flex justify-end space-x-2"></CardFooter>
      </Card>
      <ActionConfirmationModal
        title={`Are you sure you want to delete ${office.label}?`}
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={handleDelete}
      />
    </>
  );
}
